import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { RootState } from "../app/store"
import {
  Transaction,
  Bank,
  ProcessSchedule,
  Category,
  SearchList,
} from "../models/transactions.model"
import dayjs, { Dayjs } from "dayjs"

export type CategoryType = {
  category: string
  subCategory: string
  subCategory1: string
}

export type FilterType = {
  dateSelector: string
  startDate: Dayjs | null
  endDate: Dayjs | null
  searchType: string
  search: string
  isMarked: string
  accountId: string[]
  sortBy: string
  orderBy: string
  transactionType: string
  entityType: string
  paymentMethod: string
  operator: string
  category: string
  subCategory: string
  subCategory1: string
  minAmount?: string | null
  maxAmount?: string | null
  contact?: boolean | null
  removeSelfTransfer?: boolean
}

export type PaginationType = {
  page: number
  pageSize: number
}

export type reconcileType = {
  amount: number
  invoiceNumber: string
  tdsDeducted: boolean
  tdsAmountWithHeld: number
  tdsPercentage: number
  invoiceId: string
}[]

type TransactionsState = {
  transactionList: Transaction[]

  bankList: Bank[]
  isLoading: boolean
  pageInfo: {
    totalRowCount: number
  }
  pagination: PaginationType
  isUploading: boolean
  isDownloading: boolean
  isBankListLoading: boolean
  isCategorisationLoading: boolean
  isZohoSyncLoading: boolean
  isZohoSyncSuccess: boolean
  paymentMethods: { key: string; value: string }[]
  isPaymentMethodsLoading: boolean
  filters: FilterType
  issearchfilterLoading: boolean
  searchList: SearchList[]
  processScheduleData: ProcessSchedule
  isProcessScheduleLoading: boolean
  filterCount: number
  rowSelectionModel: string[]
  category: CategoryType
  categoryList: Category[]
  subCategoryList: Category[]
  subCategoryList1: Category[]
}

export type getTransactionListPayloadType = {
  payload: {
    page: number
    pageSize: number
    startDate?: string
    endDate?: string
    search: string
    searchType: string
    amount: string
    accountId?: string
    isMarked: string
    sortBy: string
    orderBy: string
    transactionType: string
    entityType: string
    paymentMethod: string
    operator: string
    category: string
    subCategory: string
    subCategory1: string
    minAmount?: string
    maxAmount?: string
    contact?: boolean | string
    removeSelfTransfer?: boolean
  }
}

export type uploadCSVFilePayloadType = {
  payload: {
    file: File
    accountId: string
  }
}

export type getCategoryListPayloadType = {
  payload: {
    level1?: string
    level2?: string
  }
}
export type CategoryAssociationPayloadType = {
  category: CategoryType
  transactions: string[]
}

export type CategoryAssociationDTO = {
  level_1: string
  level_2?: string
  level_3?: string
  transaction_ids: string[]
}

export type UpdateCategoryAssociationPayloadType = {
  categories: string[]
  transaction: string
}

export type UpdateCategoryAssociationDTO = {
  transactionId: string
  payload: {
    categories: string[]
  }
}
export type DeleteCategoryAssociationDTO = {
  transactionId: string
}

export type ZohoSyncPayloadType = {
  payload: {
    transactionId: string
  }
}

export type ZohoSyncDTO = {
  transactionId: string
}

const initialProcessScheduleState = {
  message: "",
  status: false,
  nextRun: "",
  scheduleStatus: "",
  id: "",
  name: "",
}
export const initialCategoryState = {
  category: "",
  subCategory: "",
  subCategory1: "",
}

const initialFilterState = () => ({
  accountId: [] as string[],
  amount: "",
  dateSelector: "month",
  endDate: dayjs().startOf("day"),
  entityType: "all",
  isMarked: "all",
  operator: "",
  orderBy: "desc",
  paymentMethod: "all",
  searchType: "search",
  search: "",
  sortBy: "date",
  startDate: dayjs().startOf("month"),
  transactionType: "all",
  category: "",
  subCategory: "",
  subCategory1: "",
  minAmount: null,
  maxAmount: null,
  contact: null,
  removeSelfTransfer: false,
})

export const initialPaginationState = {
  page: 0,
  pageSize: 10,
}

export const transactionsInitialState: TransactionsState = {
  transactionList: [],
  isLoading: false,
  pagination: initialPaginationState,
  pageInfo: {
    totalRowCount: 0,
  },
  isUploading: false,
  isDownloading: false,
  isCategorisationLoading: false,
  bankList: [],
  isBankListLoading: false,
  paymentMethods: [],
  isPaymentMethodsLoading: false,
  isZohoSyncLoading: false,
  isZohoSyncSuccess: false,
  filters: initialFilterState(),
  issearchfilterLoading: false,
  searchList: [],
  processScheduleData: initialProcessScheduleState,
  isProcessScheduleLoading: false,
  filterCount: 0,
  rowSelectionModel: [],
  category: initialCategoryState,
  categoryList: [],
  subCategoryList: [],
  subCategoryList1: [],
}

export const transactionsSlice = createSlice({
  name: "transactions",
  initialState: transactionsInitialState,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getTransactionList: (state, action: getTransactionListPayloadType) => {
      state.isLoading = true
    },
    getTransactionListSuccess: (
      state,
      {
        payload,
      }: { payload: { transactionList: Transaction[]; totalRowCount: number } },
    ) => {
      state.transactionList = payload.transactionList
      state.pageInfo.totalRowCount = payload.totalRowCount
      state.isLoading = false
    },
    getTransactionListFailed: (state) => {
      state.isLoading = false
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    uploadCSVFile: (state, action: uploadCSVFilePayloadType) => {
      state.isUploading = true
    },
    uploadCSVFileSuccess: (state) => {
      state.isUploading = false
    },
    uploadCSVFileFailed: (state) => {
      state.isUploading = false
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    downloadCSVFile: (state, action: getTransactionListPayloadType) => {
      state.isDownloading = true
    },
    downloadCSVFileSuccess: (state) => {
      state.isDownloading = false
    },
    downloadCSVFileFailed: (state) => {
      state.isDownloading = false
    },
    getBankList: (state) => {
      state.isBankListLoading = true
    },
    getBankListSuccess: (
      state,
      { payload }: { payload: { bankList: Bank[] } },
    ) => {
      state.bankList = payload.bankList
      state.isBankListLoading = false
    },
    getBankListFailed: (state) => {
      state.isBankListLoading = false
    },

    getPaymentMethods: (state) => {
      state.isPaymentMethodsLoading = true
    },
    getPaymentMethodsSuccess: (
      state,
      { payload }: { payload: { paymentMethods: [] } },
    ) => {
      state.paymentMethods = payload.paymentMethods
      state.isPaymentMethodsLoading = false
    },
    getPaymentMethodsFailed: (state) => {
      state.isPaymentMethodsLoading = false
    },

    setFilters: (state, { payload }) => {
      state.filters = payload
      state.pagination.page = 0
    },
    setFiltersFromURL: (state, { payload }) => {
      state.filters = payload
    },
    resetFilters: (state) => {
      state.filters = initialFilterState()
      state.pagination = initialPaginationState
    },
    setPagination: (state, { payload }) => {
      state.pagination = payload
    },
    getProcessSchedule: (state) => {
      state.isProcessScheduleLoading = true
    },
    initiateProcessSchedule: (state) => {
      state.isProcessScheduleLoading = true
    },
    reinitiateProcessSchedule: (state) => {
      state.isProcessScheduleLoading = true
    },
    getProcessScheduleSuccess: (
      state,
      {
        payload,
      }: {
        payload: {
          processScheduleData: ProcessSchedule
        }
      },
    ) => {
      state.processScheduleData = payload.processScheduleData
      state.isProcessScheduleLoading = false
    },
    getProcessScheduleFailed: (state) => {
      state.isProcessScheduleLoading = false
    },
    cancelProcessSchedule: (state) => {
      state.isProcessScheduleLoading = true
    },
    cancelProcessScheduleSuccess: (
      state,
      {
        payload,
      }: {
        payload: {
          processScheduleData: ProcessSchedule
        }
      },
    ) => {
      state.processScheduleData = payload.processScheduleData
      state.isProcessScheduleLoading = false
    },
    cancelProcessScheduleFailed: (state) => {
      state.isProcessScheduleLoading = false
    },
    setFilterCount: (state) => {
      state.filterCount = Object.keys(state.filters ?? {}).filter(
        (key: string) => {
          const filterKeys = [
            "dateSelector",
            "accountId",
            "transactionType",
            "paymentMethod",
            "operator",
            "isMarked",
            "entityType",
          ]
          if (key === "accountId" && Array.isArray(state.filters.accountId)) {
            return (state.filters.accountId as string[]).some(
              (id: string) => !initialFilterState().accountId.includes(id),
            )
          }
          return (
            filterKeys.includes(key) &&
            state.filters[key as keyof FilterType] !==
              initialFilterState()[key as keyof FilterType]
          )
        },
      ).length
    },
    setRowSelectionModel: (
      state: TransactionsState,
      {
        payload: { rowSelectionModel },
      }: { payload: { rowSelectionModel: string[] } },
    ) => {
      state.rowSelectionModel = [...rowSelectionModel]
    },
    resetRowSelectionModel: (state: TransactionsState) => {
      state.rowSelectionModel = []
    },

    setCategory: (state, { payload }) => {
      state.category = payload
    },
    ClearCategory: (state) => {
      state.category = initialCategoryState
    },
    ClearSubCategory: (state) => {
      state.category.subCategory = initialCategoryState.subCategory
      state.category.subCategory1 = initialCategoryState.subCategory1
    },
    ClearSubCategory1: (state) => {
      state.category.subCategory1 = initialCategoryState.subCategory1
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getCategoryList: (state, action: getCategoryListPayloadType) => {
      state.isLoading = true
    },
    getCategoryListSuccess: (
      state,
      { payload }: { payload: { categoryList: Category[] } },
    ) => {
      state.categoryList = payload.categoryList
      state.isLoading = false
    },
    getCategoryListFailed: (state) => {
      state.isLoading = false
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getSubCategoryList: (state, action: getCategoryListPayloadType) => {
      state.isLoading = true
    },
    getSubCategoryListSuccess: (
      state,
      { payload }: { payload: { categoryList: Category[] } },
    ) => {
      state.subCategoryList = payload.categoryList
      state.isLoading = false
    },
    getSubCategoryListFailed: (state) => {
      state.isLoading = false
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getSubCategoryList1: (state, action: getCategoryListPayloadType) => {
      state.isLoading = true
    },
    getSubCategoryList1Success: (
      state,
      { payload }: { payload: { categoryList: Category[] } },
    ) => {
      state.subCategoryList1 = payload.categoryList
      state.isLoading = false
    },
    getSubCategoryList1Failed: (state) => {
      state.isLoading = false
    },
    submitCategoryAssociationRequest: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<CategoryAssociationDTO>,
    ) => {
      state.isCategorisationLoading = true
      state.isLoading = true
    },
    submitCategoryAssociationSuccess: (state) => {
      state.isCategorisationLoading = false
      state.isLoading = false
      state.category = initialCategoryState
      state.rowSelectionModel = []
    },
    submitCategoryAssociationFailure: (state) => {
      state.isCategorisationLoading = false
      state.isLoading = false
    },
    submitUpdateCategoryAssociationRequest: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<UpdateCategoryAssociationDTO>,
    ) => {
      state.isCategorisationLoading = true
      state.isLoading = true
    },
    submitUpdateCategoryAssociationSuccess: (state) => {
      state.isCategorisationLoading = false
      state.isLoading = false
      state.category = initialCategoryState
      state.rowSelectionModel = []
    },
    submitUpdateCategoryAssociationFailure: (state) => {
      state.isCategorisationLoading = false
      state.isLoading = false
    },
    submitDeleteCategoryAssociationRequest: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<DeleteCategoryAssociationDTO>,
    ) => {
      state.isCategorisationLoading = true
      state.isLoading = true
    },
    submitDeleteCategoryAssociationSuccess: (state) => {
      state.isCategorisationLoading = false
      state.isLoading = false
      state.category = initialCategoryState
      state.rowSelectionModel = []
    },
    submitDeleteCategoryAssociationFailure: (state) => {
      state.isCategorisationLoading = false
      state.isLoading = false
    },
    getSearchList: (state) => {
      state.issearchfilterLoading = true
    },
    getSearchListSuccess: (
      state,
      { payload }: { payload: { searchList: SearchList[] } },
    ) => {
      state.searchList = payload.searchList
      state.issearchfilterLoading = false
    },
    getSearchListFailed: (state) => {
      state.issearchfilterLoading = false
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    syncZohoData: (state, action: PayloadAction<string>) => {
      state.isZohoSyncLoading = true
      state.isZohoSyncSuccess = false
    },
    syncZohoDataSuccess: (state) => {
      state.isZohoSyncLoading = false
      state.isZohoSyncSuccess = true
    },
    syncZohoDataFailed: (state) => {
      state.isZohoSyncLoading = false
      state.isZohoSyncSuccess = false
    },
  },
})

export const {
  getTransactionList,
  getTransactionListSuccess,
  getTransactionListFailed,
  uploadCSVFile,
  uploadCSVFileSuccess,
  uploadCSVFileFailed,
  downloadCSVFile,
  downloadCSVFileSuccess,
  downloadCSVFileFailed,
  getBankList,
  getBankListSuccess,
  getBankListFailed,
  getPaymentMethods,
  getPaymentMethodsSuccess,
  getPaymentMethodsFailed,
  setFilters,
  setFiltersFromURL,
  resetFilters,
  setPagination,
  getProcessSchedule,
  initiateProcessSchedule,
  reinitiateProcessSchedule,
  getProcessScheduleSuccess,
  getProcessScheduleFailed,
  cancelProcessSchedule,
  cancelProcessScheduleSuccess,
  cancelProcessScheduleFailed,
  setFilterCount,
  setRowSelectionModel,
  setCategory,
  ClearCategory,
  ClearSubCategory,
  ClearSubCategory1,
  getCategoryList,
  getCategoryListSuccess,
  getCategoryListFailed,
  getSubCategoryList,
  getSubCategoryList1,
  getSubCategoryList1Failed,
  getSubCategoryList1Success,
  getSubCategoryListFailed,
  getSubCategoryListSuccess,
  submitCategoryAssociationRequest,
  submitCategoryAssociationSuccess,
  submitCategoryAssociationFailure,
  submitUpdateCategoryAssociationRequest,
  submitUpdateCategoryAssociationFailure,
  submitUpdateCategoryAssociationSuccess,
  submitDeleteCategoryAssociationFailure,
  submitDeleteCategoryAssociationRequest,
  submitDeleteCategoryAssociationSuccess,
  resetRowSelectionModel,
  getSearchList,
  getSearchListSuccess,
  getSearchListFailed,
  syncZohoData,
  syncZohoDataSuccess,
  syncZohoDataFailed,
} = transactionsSlice.actions

export const selectTransactions = (state: RootState) => state.transactions

export const selectSortModel = (state: RootState) => {
  if (state.transactions.filters.sortBy === "none") {
    return []
  } else {
    return [
      {
        field: state.transactions.filters.sortBy,
        sort: state.transactions.filters.orderBy,
      },
    ]
  }
}

export default transactionsSlice.reducer
